import { CloseOutlined } from '@ant-design/icons';
import { useState } from 'react';
import styled from 'styled-components';

import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Input from '@/components/Inputs/Input';
import PasswordInput from '@/components/Inputs/PasswordInput';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { modalStyle } from '@/styles/style';

import { IMPORT_STATE, type OkendoInfo } from '../types';

type Props = {
  onConnectComplete: (workspace: OkendoInfo) => void;
};

const OkendoTokenModal = (props: Props) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const color = useColor();
  const { closeModal } = useModalContext();

  const [userId, setUserId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const onConnectClick = async () => {
    if (userId.trim() === '' || apiKey.trim() === '') return;
    if (!currWorkspace) return;
    const data = await service.okendo.createOkendoItem(
      userId,
      apiKey,
      currWorkspace.alias,
    );
    if (data) {
      props.onConnectComplete({
        workspace_id: data.workspace_id || data.alias,
        alias: data.alias,
        import_state:
          data.import_state || data.state || IMPORT_STATE.NOT_STARTED,
        history: data.history || [],
      });
      setConnectionError(null);
      closeModal();
    } else {
      setConnectionError('Your access token is invalid. Please check again!');
    }
  };

  return (
    <ModalStyle d="column">
      <Flex j="space-between">
        <VFlex g={8}>
          <Heading type="h2">Connect Okendo</Heading>
          <Typo type="label2" color={color.gray700}>
            Okendo user ID and API key are required to connect to Okendo.
          </Typo>
        </VFlex>
        <IconButton
          icon={<CloseOutlined />}
          outlined={false}
          sizeType="l"
          onClick={closeModal}
        />
      </Flex>
      <Space h="40px" />

      <Typo type="label2" weight="sb">
        Okendo user ID
      </Typo>
      <Space h="12px" />
      <Input
        placeholder="type Okendo user ID"
        sizeType="m"
        value={userId}
        onChange={(e) => setUserId(e.target.value)}
      />
      <Space h="24px" />
      <Typo type="label2" weight="sb">
        Okendo API key
      </Typo>
      <Space h="12px" />
      <PasswordInput
        placeholder="type Okendo API key"
        sizeType="m"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
      />
      <Space h="32px" />
      {connectionError ? (
        <Flex j="space-between" a="center">
          <Typo type="label2" style={{ color: color.red600 }}>
            {connectionError}
          </Typo>
          <Button
            sizeType="m"
            onClick={onConnectClick}
            disabled={userId.trim() === '' || apiKey.trim() === ''}
          >
            Connect
          </Button>
        </Flex>
      ) : (
        <Flex j="flex-end">
          <Button
            sizeType="m"
            onClick={onConnectClick}
            disabled={userId.trim() === '' || apiKey.trim() === ''}
          >
            Connect
          </Button>
        </Flex>
      )}
    </ModalStyle>
  );
};

const ModalStyle = styled(Flex)`
  width: 620px;
  padding: 32px;
  color: ${(props) => props.theme.colors.gray800};
  ${modalStyle};
`;

export default OkendoTokenModal;
